import { useState } from "react";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useGetMyTitlesQuery } from "../../api/personalApi";
import { useSetAvatarTitleMutation } from "../../api/authApi";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../components/ui/Toast";
import ConnectionState from "../../components/ui/ConnectionState";
import { SoftButton, cls, useDialogPaperSx } from "../hatim/soft/SoftKit";
import { FamilyIcon, Medal, shade, titlePercent } from "./titleParts";
import { titleImageOf, tierLabelKey } from "../../lib/titleImage";
import {
  amountText,
  conditionRatio,
  conditionText,
  limitingCondition,
  remainingText,
  titleConditions,
} from "../../lib/titleProgress";
import "../profile/darkSurface.css";

/** Kazanılma tarihi: "15 Ekim 2026" biçiminde; geçersizse null. */
export function formatEarnedAt(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
}

/**
 * Kazanılmış ünvanı profil resmi yapar ya da profil resminden kaldırır.
 * Şu anki seçim oturumdaki kullanıcının avatarTitle kodundan okunur.
 */
export function AvatarTitleAction({ title }) {
  const { t } = useScriptMode();
  const toast = useToast();
  const { user } = useAuth();
  const [setAvatarTitle, { isLoading }] = useSetAvatarTitleMutation();
  const isCurrent = Boolean(title?.code) && user?.avatarTitle === title.code;

  const handleClick = async () => {
    try {
      await setAvatarTitle(isCurrent ? {} : { code: title.code }).unwrap();
      toast.success(t(isCurrent ? "titles_avatar_cleared" : "titles_avatar_saved"));
    } catch (err) {
      toast.error(err?.message || err?.data?.message || t("titles_avatar_error"));
    }
  };

  return (
    <SoftButton
      tone={isCurrent ? "soft" : "primary"}
      onClick={handleClick}
      disabled={isLoading}
      data-avatar-action={isCurrent ? "clear" : "set"}
      sx={{ mt: 1 }}
    >
      {t(isCurrent ? "titles_avatar_clear" : "titles_avatar_set")}
    </SoftButton>
  );
}

/**
 * Rozete basınca: ne için verildiği, eşiği ve kilitliyse ne kadar kaldığı.
 * canSetAvatar: kendi ünvanlarında kazanılmış rozet profil resmi yapılabilir.
 */
export function TitleDialog({ title, unit, onClose, canSetAvatar = false }) {
  const { t } = useScriptMode();
  const paperSx = useDialogPaperSx();
  const open = Boolean(title);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: paperSx }}>
      {title ? (
        <>
          <div className="flex items-center justify-between px-5 pt-4">
            <span className={`text-[15px] font-medium ${cls.title}`}>{t("titles_dialog_title")}</span>
            <IconButton size="small" onClick={onClose} aria-label={t("close")}>
              <CloseIcon fontSize="small" className={cls.muted} />
            </IconButton>
          </div>
          <DialogContent>
            <div className="flex flex-col items-center gap-2 pb-2 text-center" data-testid="title-dialog">
              <Medal
                title={title}
                family={title.family}
                size={titleImageOf(title, title.family) ? 128 : 72}
              />
              <div className={`text-lg font-medium ${cls.title}`}>{title.name}</div>
              {tierLabelKey(title.tier) ? (
                <span className={`text-xs tracking-wide ${cls.muted}`} data-testid="title-tier">
                  {t(tierLabelKey(title.tier))}
                </span>
              ) : null}
              <span
                className={`px-3 py-0.5 rounded-full text-xs font-medium ${
                  title.earned ? "" : `${cls.chip}`
                }`}
                style={title.earned ? { backgroundColor: `${title.color}22`, color: title.color } : undefined}
              >
                {title.earned ? t("titles_earned") : t("titles_locked")}
              </span>
              {title.earned && formatEarnedAt(title.earnedAt) ? (
                <div className={`text-[13px] ${cls.muted}`}>
                  {t("titles_earned_on", { date: formatEarnedAt(title.earnedAt) })}
                </div>
              ) : null}
              <p className={`text-sm leading-5 ${cls.muted}`}>{title.description}</p>
              {title.meaning ? (
                <p
                  data-testid="title-meaning"
                  className="self-stretch mt-1 px-3.5 py-2.5 rounded-xl text-[13px] leading-5 text-left bg-terakkiBrandCloud text-terakkiInk dark:bg-white/[0.06] dark:text-white/80"
                >
                  {title.meaning}
                </p>
              ) : null}
              <div className={`self-stretch h-2 mt-1 rounded-full overflow-hidden ${cls.track}`}>
                <div
                  data-progress
                  className="h-full rounded-full"
                  style={{ width: `${titlePercent(title)}%`, backgroundColor: title.color }}
                />
              </div>
              {title.threshold != null
                ? titleConditions(title, unit).map((condition, i) => (
                    <div key={condition.metric || i} className="flex flex-col items-center" data-condition={i}>
                      <div className={`text-[13px] font-medium ${cls.title}`}>{conditionText(condition)}</div>
                      {title.earned || conditionRatio(condition) >= 1 ? null : (
                        <div className={`text-[13px] ${cls.muted}`}>
                          {`${t("titles_remaining")}: ${remainingText(condition)}`}
                        </div>
                      )}
                    </div>
                  ))
                : null}
              {canSetAvatar && title.earned ? <AvatarTitleAction title={title} /> : null}
            </div>
          </DialogContent>
        </>
      ) : null}
    </Dialog>
  );
}

/** Ailenin rozetleri: kazanılanlar renkli, kilitliler soluk ve altında ince ilerleme. */
export function FamilyRow({ family, onSelect }) {
  return (
    <section className={`px-5 py-4 flex flex-col gap-3 ${cls.row}`} data-family={family.key}>
      <div className="flex items-baseline justify-between gap-3">
        <h2 className={`text-[15px] font-medium ${cls.title}`}>{family.name}</h2>
        <span className={`text-[13px] font-medium ${cls.accent}`}>{amountText(family.value, family.unit)}</span>
      </div>
      <ul className="flex flex-wrap gap-x-3 gap-y-4">
        {(family.titles || []).map((title) => (
          <li key={title.code}>
            <button
              type="button"
              data-title={title.code}
              onClick={() => onSelect(title)}
              className="w-[76px] flex flex-col items-center gap-1.5 rounded-lg py-1 hover:bg-terakkiBrandCloud dark:hover:bg-white/[0.06] transition-colors"
            >
              <Medal title={title} family={family.key} />
              <span
                className={`w-full text-[11.5px] font-medium text-center truncate ${
                  title.earned ? cls.title : cls.muted
                }`}
              >
                {title.name}
              </span>
              {title.earned ? null : (
                <span className={`block w-11 h-1 rounded-full overflow-hidden ${cls.track}`}>
                  <span
                    className="block h-full rounded-full"
                    style={{ width: `${titlePercent(title)}%`, backgroundColor: title.color }}
                  />
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function TitlesSkeleton() {
  return (
    <div>
      {[0, 1, 2].map((i) => (
        <div key={i} className={`px-5 py-4 flex flex-col gap-3 ${cls.row}`}>
          <div className="flex justify-between">
            <span className={`h-4 w-24 rounded ${cls.skeleton}`} />
            <span className={`h-4 w-12 rounded ${cls.skeleton}`} />
          </div>
          <div className="flex gap-3">
            {[0, 1, 2, 3, 4].map((j) => (
              <span key={j} className={`w-[58px] h-[58px] rounded-full ${cls.skeleton}`} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Ailenin kartı: başlık, değer, sıradaki eşiğe ilerleme ve madalyalar. */
function FamilyCard({ family, onSelect, index, t }) {
  const titles = family.titles || [];
  const earned = titles.filter((x) => x.earned).length;
  const next = titles.find((x) => !x.earned) || null;
  const pct = next ? titlePercent(next) : 100;
  const nextLimit = next ? limitingCondition(next, family.unit) : null;
  return (
    <section
      data-family={family.key}
      className="rounded-2xl border border-terakkiBorder bg-terakkiSurface p-4 sm:p-5 grid grid-cols-1 lg:grid-cols-[minmax(260px,340px)_minmax(0,1fr)] gap-4 lg:gap-6 items-center min-w-0 motion-safe:animate-rise-in dark:bg-white/5 dark:border-white/15"
      style={{ animationDelay: `${0.12 + index * 0.06}s` }}
    >
      {/* Sol: aile bilgisi ve sıradaki ünvana ilerleme */}
      <div className="flex flex-col gap-3 min-w-0">
      <header className="flex items-start gap-3">
        <span className="w-11 h-11 rounded-xl bg-terakkiBrandCloud text-terakkiPrimaryDark flex items-center justify-center shrink-0 dark:bg-white/10 dark:text-white">
          <FamilyIcon family={family.key} aria-hidden />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className={`text-md font-SemiBold break-words ${cls.title}`}>{family.name}</h2>
          <div className={`text-base ${cls.muted}`}>{`${earned}/${titles.length} ${t("titles_family_earned")}`}</div>
        </div>
        <span className={`text-md font-SemiBold tabular-nums shrink-0 ${cls.accent}`}>
          {amountText(family.value, family.unit)}
        </span>
      </header>

      {next ? (
        <div className="flex flex-col gap-1.5">
          <div className={`flex flex-wrap justify-between gap-x-3 text-base ${cls.muted}`}>
            <span className="min-w-0">
              {t("titles_next")}: <span className={cls.title}>{next.name}</span>
            </span>
            <span className="tabular-nums">{`${remainingText(nextLimit)} ${t("titles_left")}`}</span>
          </div>
          <div className={`h-2 rounded-full overflow-hidden ${cls.track}`}>
            <div
              className="h-full rounded-full transition-[width] duration-700 ease-out"
              style={{ width: `${pct}%`, backgroundColor: next.color }}
            />
          </div>
        </div>
      ) : (
        <div className={`text-base ${cls.accent}`}>{t("titles_family_complete")}</div>
      )}
      </div>

      {/* Sağ: madalyalar tek satırda, yatay kaydırılır */}
      <ul className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x min-w-0">
        {titles.map((title, i) => (
          <li
            key={title.code}
            className="w-[92px] shrink-0 snap-start motion-safe:animate-pop-in"
            style={{ animationDelay: `${0.2 + index * 0.06 + Math.min(i, 12) * 0.04}s` }}
          >
            <button
              type="button"
              data-title={title.code}
              onClick={() => onSelect(title)}
              className="w-full flex flex-col items-center gap-1.5 rounded-xl py-2 hover:bg-terakkiBrandCloud dark:hover:bg-white/[0.06] transition-colors"
            >
              <Medal title={title} family={family.key} size={64} />
              <span
                className={`w-full px-1 text-sm font-medium text-center leading-tight line-clamp-2 break-words ${
                  title.earned ? cls.title : cls.muted
                }`}
              >
                {title.name}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Ünvanlarım (mobil profile/titles): sayfaya yayılan düzen. Üstte vitrin
 * (en yüksek ünvan, kazanılan/toplam, genel ilerleme, kazanılmaya en yakın
 * ünvan), altında her aile kendi kartında; geniş ekranda iki-üç sütun.
 * Ünvanlar kayıtlardan türetilir, kullanıcı ayrıca bir şey yapmaz.
 */
export default function TitlesPage() {
  const { t } = useScriptMode();
  const [selected, setSelected] = useState(null);
  const { data: resp, isLoading, isError, error, isFetching, refetch } = useGetMyTitlesQuery();
  const data = resp?.data;
  const families = data?.families || [];
  const highest = data?.highest || null;
  const unitOf = (key) => families.find((f) => f.key === key)?.unit || "";

  const all = families.flatMap((f) => (f.titles || []).map((x) => ({ ...x, family: x.family || f.key, unit: x.unit || f.unit })));
  const total = all.length;
  const earnedCount = data?.earnedCount ?? all.filter((x) => x.earned).length;
  const overall = total > 0 ? Math.round((earnedCount / total) * 100) : 0;
  // Kazanılmaya en yakın kilitli ünvan.
  const closest = all.filter((x) => !x.earned).sort((a, b) => titlePercent(b) - titlePercent(a))[0] || null;

  return (
    <div className="tk-dark w-full flex flex-col gap-4 sm:gap-5">
      {/* Vitrin */}
      <section
        className="relative overflow-hidden rounded-3xl p-5 sm:p-7 text-white bg-gradient-to-br from-terakkiBrandDeep via-terakkiBrand to-terakkiBrandSoft motion-safe:animate-rise-in dark:from-[#0B4B50] dark:via-[#11605F] dark:to-[#1C7A77]"
      >
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10" />
        <div className="pointer-events-none absolute -left-10 -bottom-20 h-48 w-48 rounded-full bg-white/10" />
        <div className="relative grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6 items-center">
          <div className="flex items-center gap-4 min-w-0">
            {titleImageOf(highest) ? (
              <span
                className="w-20 h-20 sm:w-24 sm:h-24 shrink-0 motion-safe:animate-pop-in drop-shadow-lg"
                style={{ animationDelay: "0.1s" }}
              >
                <img
                  src={titleImageOf(highest)}
                  alt={highest.name || ""}
                  className="block w-full h-full object-contain select-none"
                  draggable={false}
                />
              </span>
            ) : (
            <span
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-full p-1 bg-white/20 shrink-0 motion-safe:animate-pop-in"
              style={{ animationDelay: "0.1s" }}
            >
              <span
                className="w-full h-full rounded-full flex items-center justify-center shadow-lg"
                style={{
                  background: highest ? `linear-gradient(160deg, ${highest.color}, ${shade(highest.color)})` : "rgba(255,255,255,0.15)",
                }}
              >
                {highest ? (
                  <FamilyIcon family={highest.family} sx={{ fontSize: 40 }} aria-hidden />
                ) : (
                  <WorkspacePremiumIcon sx={{ fontSize: 40 }} aria-hidden />
                )}
              </span>
            </span>
            )}
            <div className="min-w-0">
              <h1 className="text-base text-white/80">{t("titles_my_title")}</h1>
              {isLoading ? (
                <span className="block h-7 w-44 my-1 rounded bg-white/20 animate-pulse" />
              ) : (
                <div className="text-4xl sm:text-5xl font-SemiBold leading-tight break-words">
                  {highest?.name || t("titles_none")}
                </div>
              )}
              <div className="text-base text-white/85 mt-1">
                {isLoading ? "\u00A0" : t("titles_earned_of_total", { count: earnedCount, total })}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 min-w-0">
            <div>
              <div className="flex justify-between text-base text-white/85 mb-1.5">
                <span>{t("titles_overall")}</span>
                <span className="tabular-nums">%{isLoading ? 0 : overall}</span>
              </div>
              <div className="h-2.5 rounded-full bg-white/20 overflow-hidden">
                <div
                  className="h-full rounded-full bg-white transition-[width] duration-1000 ease-out"
                  style={{ width: `${isLoading ? 0 : overall}%` }}
                />
              </div>
            </div>
            {closest ? (
              <button
                type="button"
                onClick={() => setSelected(closest)}
                className="flex items-center gap-3 rounded-2xl bg-white/15 hover:bg-white/20 transition-colors p-3 text-left"
              >
                <Medal title={closest} family={closest.family} size={48} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white/80">{t("titles_closest")}</div>
                  <div className="text-md font-SemiBold break-words">{closest.name}</div>
                  <div className="text-sm text-white/80 tabular-nums">
                    {`${conditionText(limitingCondition(closest))} · %${titlePercent(closest)}`}
                  </div>
                </div>
              </button>
            ) : null}
          </div>
        </div>
      </section>

      {/* Aileler */}
      {isLoading ? (
        <div className="flex flex-col gap-4 sm:gap-5">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={`${cls.panel} overflow-hidden`}>
              <TitlesSkeleton />
            </div>
          ))}
        </div>
      ) : isError || !data ? (
        <ConnectionState
          error={isError ? error : null}
          message={t("titles_load_error")}
          onRetry={() => refetch()}
          retrying={isFetching}
        />
      ) : (
        <div className="flex flex-col gap-4 sm:gap-5">
          {families.map((family, index) => (
            <FamilyCard key={family.key} family={family} onSelect={setSelected} index={index} t={t} />
          ))}
        </div>
      )}

      <TitleDialog
        title={selected}
        unit={unitOf(selected?.family)}
        onClose={() => setSelected(null)}
        canSetAvatar
      />
    </div>
  );
}
